/* eslint-disable max-len */
import {
  Bank, FileSignature, FileSpreadsheet,
} from '@huspy/briks-icons';
import { AnyRoute } from '@tanstack/react-router';
import {
  useMemo, JSX, useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import { ProposalCreationEngine } from '@modules/proposals/presentation/v2/pages/ProposalCreation/hooks/useProposalCreationStateMachineEngine';
import {
  ProposalCreationBankProductsRoute, ProposalCreationClientAndMortgageRoute, ProposalCreationConfiguratorRoute
} from '@modules/proposals/presentation/routes';

export namespace ProposalCreationAsideLink {
  export type Props<T extends ProposalCreationEngine.StateMachineRoutes> = {
    icon: JSX.Element;
    name: string;
    to: T['to'];
    search?: T['types']['searchSchema'];
    params?: T['types']['allParams'];
    isAccessible: boolean;
    isComplete?: boolean;
    subtitle?: string;
    sublinks: {
      name: string;
      to?: T['to'] | `${T['to']}${string}`;
      search: T['types']['searchSchema'];
      params: T['types']['allParams'];
      isAccessible?: boolean;
      isComplete?: boolean;
    }[];
    accessibleText?: string;
  };

  // eslint-disable-next-line max-len
  export type FactoryArgs<T extends AnyRoute> = Omit<ProposalCreationAsideLink.Props<T>, 'to' | 'isAccessible' | 'isComplete' | 'sublinks'> & {
    route: T;
    sublinks: (Omit<ProposalCreationAsideLink.Props<T>['sublinks'][number], 'to'> & {
      to?: `${T['to']}${string}`
    })[];
  };
}

const useGetProposalCreationAsideLinks = ({ stateMachine }: {
  stateMachine: ProposalCreationEngine.StateMachine;
}) => {
  const { t } = useTranslation();

  // eslint-disable-next-line max-len
  const linkFactory = useCallback(<T extends ProposalCreationEngine.StateMachineRoutes>(
    obj: ProposalCreationAsideLink.FactoryArgs<T>): ProposalCreationAsideLink.Props<T> => {
    const { route } = obj;
    return {
      ...obj,
      to: route.to,
      isAccessible: stateMachine[route.to]?.isAccessible,
      isComplete: stateMachine[route.to]?.isComplete,
      sublinks: obj.sublinks?.map((sub) => ({
        ...sub,
        isAccessible: stateMachine[sub.to! as keyof ProposalCreationEngine.StateMachine]?.isAccessible,
        isComplete: stateMachine[sub.to! as keyof ProposalCreationEngine.StateMachine]?.isComplete,
      })),
      accessibleText: obj?.accessibleText,
    };
  }, [stateMachine]);

  const asideLinks = useMemo(() => [
    linkFactory({
      route: ProposalCreationClientAndMortgageRoute,
      icon: <FileSignature height={ 20 } width={ 20 } color='inherit' />,
      name: t('proposals.asideContent.steps.clientAndMortgage.title'),
      // subtitle: `${stateMachine[ProposalCreationClientAndMortgageRoute.to].stateMetadata?.completedSteps}/${stateMachine[ProposalCreationClientAndMortgageRoute.to].stateMetadata?.totalSteps} fields`,
      sublinks: [],
    }),
    linkFactory({
      route: ProposalCreationBankProductsRoute,
      icon: <Bank height={ 20 } width={ 20 } color='inherit' />,
      name: t('proposals.asideContent.steps.bankProducts.title'),
      sublinks: [],
    }),
    linkFactory({
      route: ProposalCreationConfiguratorRoute,
      icon: <FileSpreadsheet height={ 20 } width={ 20 } color='inherit' />,
      name: t('proposals.asideContent.steps.review.title'),
      sublinks: [],
      accessibleText: 'Customize',
    })
  ], [linkFactory, t]);

  return { asideLinks };
};

export default useGetProposalCreationAsideLinks;
